import React, { useState, useEffect } from 'react';
import { LuSearch, LuPlus, LuFilter, LuTrash2, LuUserX, LuRefreshCw } from 'react-icons/lu';
import { useAuth } from '../hooks/useAuth';
import { teamService } from '../services/teamService';
import MemberModal from '../components/teams/MemberModal';
import AddMemberModal from '../components/teams/AddMemberModal';
import ResetPasswordModal from '../components/teams/ResetPasswordModal';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const Teams = () => {
    const { userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);

    // UI State
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name'); // name, role, status
    const [selectedMember, setSelectedMember] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [memberToReset, setMemberToReset] = useState(null);

    // Permissions
    const isAdmin = userProfile?.role === 'Admin';
    const isManager = userProfile?.role === 'Manager';
    const canEdit = isAdmin || isManager;

    useEffect(() => {
        fetchMembers();
    }, []);

    useEffect(() => {
        filterAndSortMembers();
    }, [members, searchTerm, sortBy]);

    const fetchMembers = async () => {
        setLoading(true);
        const result = await teamService.getTeamMembers();
        if (result.success) {
            setMembers(result.data);
        } else {
            toast.error('Failed to load team members');
        }
        setLoading(false);
        toast.dismiss();
    };

    const filterAndSortMembers = () => {
        let result = [...members];

        // Search
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.full_name?.toLowerCase().includes(lowerTerm) ||
                m.email?.toLowerCase().includes(lowerTerm) ||
                m.role?.toLowerCase().includes(lowerTerm)
            );
        }

        // Sort
        result.sort((a, b) => {
            if (sortBy === 'name') return a.full_name?.localeCompare(b.full_name || '');
            if (sortBy === 'role') return a.role?.localeCompare(b.role || '');
            if (sortBy === 'status') {
                const statusA = a.deleted_at ? 'inactive' : 'active';
                const statusB = b.deleted_at ? 'inactive' : 'active';
                return statusA.localeCompare(statusB);
            }
            return 0;
        });

        setFilteredMembers(result);
    };

    const handleInvite = () => {
        if (!isAdmin) {
            toast.error('Only Admins can invite new members');
            return;
        }
        setIsInviteModalOpen(true);
    };

    const handleMemberClick = (member) => {
        // Only allow viewing if Admin, Manager, or Self (though UI might hide others anyway)
        // RLS protects data, but UI should also be smart.
        // Assuming current list is already RLS filtered.
        setSelectedMember(member);
        setIsModalOpen(true);
    };

    const handleMemberUpdate = (updatedMember) => {
        setMembers(prev => prev.map(m => m.id === updatedMember.id ? updatedMember : m));
    };

    const handleSoftDelete = async (e, memberId) => {
        e.stopPropagation();
        if (!isAdmin) return;

        if (!window.confirm('Are you sure you want to delete this user? (Soft Delete)')) return;

        const result = await teamService.softDeleteMember(memberId);
        if (result.success) {
            toast.success('User deactivated');
            fetchMembers(); // Refresh to see status change
        } else {
            toast.error('Failed to deactivate user');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage your team, roles, and permissions.
                    </p>
                </div>

                {isAdmin && (
                    <button
                        onClick={handleInvite}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <SafeIcon icon={LuPlus} className="mr-2 h-4 w-4" />
                        Add Member
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow sm:flex sm:items-center sm:justify-between gap-4">
                <div className="relative flex-1 max-w-lg">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={LuSearch} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                </div>

                <div className="flex items-center gap-2 mt-4 sm:mt-0">
                    <span className="text-sm text-gray-500">Sort by:</span>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        <option value="name">Name</option>
                        <option value="role">Role</option>
                        <option value="status">Status</option>
                    </select>
                </div>
            </div>

            {/* Members Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredMembers.map((member) => (
                    <div
                        key={member.id}
                        onClick={() => handleMemberClick(member)}
                        className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow border-l-4 ${member.deleted_at ? 'border-gray-300 opacity-75' : 'border-blue-500'
                            }`}
                    >
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className={`h-12 w-12 rounded-full flex items-center justify-center text-lg font-bold border-2 ${member.deleted_at ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-blue-50 text-blue-600 border-blue-100'
                                        }`}>
                                        {member.full_name?.charAt(0) || '?'}
                                    </div>
                                </div>
                                <div className="ml-5 flex-1">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 truncate">
                                            {member.full_name}
                                        </h3>
                                        {member.deleted_at && (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 text-sm text-gray-500 truncate">
                                        {member.email}
                                    </div>
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.role === 'Admin' ? 'bg-purple-100 text-purple-800' :
                                            member.role === 'Manager' ? 'bg-blue-100 text-blue-800' :
                                                'bg-green-100 text-green-800'
                                            }`}>
                                            {member.role}
                                        </span>

                                        {isAdmin && !member.deleted_at && (
                                            <div className="flex items-center">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMemberToReset(member);
                                                        setIsResetModalOpen(true);
                                                    }}
                                                    className="text-gray-400 hover:text-blue-600 transition-colors p-1 mr-1"
                                                    title="Reset Password"
                                                >
                                                    <SafeIcon icon={LuRefreshCw} className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => handleSoftDelete(e, member.id)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                    title="Deactivate User"
                                                >
                                                    <SafeIcon icon={LuTrash2} className="h-4 w-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredMembers.length === 0 && (
                <div className="text-center py-12">
                    <SafeIcon icon={LuUserX} className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No members found</h3>
                    <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
                </div>
            )}

            {/* Details Modal */}
            <MemberModal
                member={selectedMember}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onUpdate={handleMemberUpdate}
                canEdit={canEdit}
            />

            {/* Add Member Modal */}
            <AddMemberModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                onInviteSuccess={() => {
                    fetchMembers();
                }}
            />

            {/* Reset Password Modal */}
            <ResetPasswordModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                userId={memberToReset?.id}
                memberName={memberToReset?.full_name}
            />
        </div>
    );
};

export default Teams;
